import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { formatQueryParams } from '@utils/Format'
import { atendimento } from '@modules/atendimento/constants/URL'
import { MENSAGENS_PER_PAGE } from '@modules/atendimento/constants/Text'
import defaultApi from '@plugins/axios'

const FALLBACK_MESSAGE = 'Erro inesperado'

class ApiAtendimento extends APITemplate {
  fallback(message = FALLBACK_MESSAGE) {
    return this.buildErrorResponse({ message, value: null })
  }

  buildMediaUrl(id_mensagem) {
    const baseURL = defaultApi.defaults.baseURL || ''
    return baseURL + atendimento.mediaProxy(id_mensagem)
  }

  async fetchMediaBlob({ url } = {}) {
    try {
      const response = await defaultApi.get(url, { responseType: 'blob' })
      return this.buildSuccessResponse({ value: { blob: response.data } })
    } catch (error) {
      return this.buildErrorResponse({
        message: (error && error.message) || 'Erro ao carregar mídia',
        value: null,
      })
    }
  }

  async listarConexoes() {
    const r = await con.get(atendimento.conexoes())
    return r || this.fallback()
  }

  async syncContatos({ id_conexao } = {}) {
    const r = await con.post(atendimento.syncContatos(id_conexao))
    return r || this.fallback()
  }

  async syncChats({ id_conexao } = {}) {
    const r = await con.post(atendimento.syncChats(id_conexao))
    return r || this.fallback()
  }

  async refreshChatsList({ id_conexao } = {}) {
    const r = await con.post(atendimento.refreshChatsList(id_conexao))
    return r || this.fallback()
  }

  async listarConversas({
    status = '',
    search = '',
    page = 1,
    per_page = 50,
    id_conexao = null,
    id_cliente_app = null,
    telefone = null,
  } = {}) {
    const params = { status, search, page, per_page }
    if (id_conexao) params.id_conexao = id_conexao
    // Escopo da aba CRM: filtra a lista para o 1:1 do cliente + grupos dele.
    if (id_cliente_app) params.id_cliente_app = id_cliente_app
    if (telefone) params.telefone = telefone
    const r = await con.get(atendimento.conversas() + formatQueryParams(params))
    return r || this.fallback()
  }

  async listarMensagens({
    id_conversa,
    before_id = null,
    page = 1,
    per_page = MENSAGENS_PER_PAGE,
  } = {}) {
    const params = { before_id, page, per_page }
    const r = await con.get(
      atendimento.mensagens(id_conversa) + formatQueryParams(params)
    )
    return r || this.fallback()
  }

  // M2 — lista TODAS as notas internas (is_note=true) da conversa,
  // independente da janela paginada de mensagens. §7.3.
  async listarNotasConversa({ id_conversa } = {}) {
    const r = await con.get(atendimento.notasConversa(id_conversa))
    return r || this.fallback()
  }

  // M1 — vínculos tarefa↔mensagem da conversa. Backend retorna
  // value: [{ externalId, taskId }]. §7.2.
  async listarVinculosTarefa({ id_conversa } = {}) {
    const url =
      atendimento.vinculosTarefa() +
      formatQueryParams({ idConversa: id_conversa })
    const r = await con.get(url)
    return r || this.fallback()
  }

  async enviarMensagem({
    id_conversa,
    tipo,
    conteudo,
    is_note = false,
    reply_to_message_id = null,
    media_url = null,
    media_mime_type = null,
    media_filename = null,
    media_size_bytes = null,
  } = {}) {
    const data = { tipo, conteudo }
    if (is_note) data.is_note = true
    if (reply_to_message_id) data.reply_to_message_id = reply_to_message_id
    if (media_url) {
      data.media_url = media_url
      data.media_mime_type = media_mime_type
      data.media_filename = media_filename
      data.media_size_bytes = media_size_bytes
    }
    const r = await con.post(atendimento.mensagens(id_conversa), data)
    return r || this.fallback()
  }

  /**
   * Upload media file to S3 via dedicated endpoint.
   * Uses XMLHttpRequest for upload progress tracking.
   *
   * @param {Object} params
   * @param {number} params.id_conversa - Conversation ID
   * @param {string} params.tipo - File type (imagem, audio, video, documento)
   * @param {File} params.arquivo - File object to upload
   * @param {Function} params.onProgress - Progress callback (0-100)
   * @returns {Promise<Object>} API response with media_url
   */
  uploadMedia({ id_conversa, tipo, arquivo, onProgress } = {}) {
    return new Promise((resolve) => {
      const formData = new FormData()
      formData.append('arquivo', arquivo)
      formData.append('tipo', tipo)
      formData.append('id_conversa', id_conversa)

      const xhr = new XMLHttpRequest()
      const baseURL = defaultApi.defaults.baseURL || ''

      xhr.open('POST', baseURL + atendimento.mediaUpload())

      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            onProgress(percent)
          }
        }
      }

      xhr.withCredentials = true

      xhr.onload = () => {
        try {
          const response = JSON.parse(xhr.responseText)
          resolve(response)
        } catch {
          // Body não-JSON (ex.: 502 proxy HTML): converte pra shape
          // padrão de erro usado pelo resto da API pra compatibilidade
          // com o `$showSnackModal`.
          resolve({
            status: 500,
            message: 'Erro ao processar resposta',
            value: null,
          })
        }
      }

      xhr.onerror = () => {
        resolve({
          status: 500,
          message: 'Erro de rede ao enviar arquivo',
          value: null,
        })
      }

      xhr.send(formData)
    })
  }

  async editarMensagem({ id_mensagem, conteudo } = {}) {
    const r = await con.put(atendimento.editarMensagem(id_mensagem), {
      conteudo,
    })
    return r || this.fallback()
  }

  async removerMensagem({ id_mensagem } = {}) {
    const r = await con.delet(atendimento.editarMensagem(id_mensagem))
    return r || this.fallback()
  }

  async importarHistorico({
    id_conversa,
    quantidade = 100,
    reload = false,
  } = {}) {
    const r = await con.post(atendimento.importarHistorico(id_conversa), {
      quantidade,
      reload,
    })
    return r || this.fallback()
  }

  // Importa do Evolution o lote de mensagens ANTERIORES à mais antiga já no
  // banco (cursor `before_ts` em epoch segundos). Usado no scroll-até-o-topo
  // quando o banco esgota. Resposta (HTTP 200 always):
  //   value: { imported, oldest_ts, has_more_whatsapp }
  async importarAnteriores({ id_conversa, before_ts = null, batch = 100 } = {}) {
    if (!id_conversa) {
      return this.fallback('Conversa inválida para importar mensagens.')
    }
    const r = await con.post(atendimento.importarAnteriores(id_conversa), {
      before_ts,
      batch,
    })
    return r || this.fallback()
  }

  async novaConversa({ id_conexao, telefone, nome } = {}) {
    const r = await con.post(atendimento.novaConversa(), {
      id_conexao,
      telefone,
      nome,
    })
    return r || this.fallback()
  }

  async reagirMensagem({ id_mensagem, emoji } = {}) {
    const r = await con.post(atendimento.reagirMensagem(id_mensagem), { emoji })
    return r || this.fallback()
  }

  async buscarClientes({ q, id_conexao = null } = {}) {
    const params = { q }
    if (id_conexao) params.id_conexao = id_conexao
    const r = await con.get(
      atendimento.buscarClientes() + formatQueryParams(params)
    )
    return r || this.fallback()
  }

  async vincularContato({ id_contato, id_cliente_app } = {}) {
    const r = await con.post(atendimento.vincularContato(id_contato), {
      id_cliente_app,
    })
    return r || this.fallback()
  }

  async desvincularContato({ id_contato } = {}) {
    const r = await con.delet(atendimento.vincularContato(id_contato))
    return r || this.fallback()
  }

  /**
   * Auto-link de contato (individual ou grupo) ao cliente da carteira do
   * profissional via match de telefone. Backend POST /contatos/<id>/auto-link.
   *
   * Resposta segue contrato congelado em ARCHITECT.md §1.1:
   *
   *   { status: 200, message: string, value: AutoLinkValue | null }
   *
   * onde AutoLinkValue é union discriminada pelo campo `mode`:
   *
   *   - { mode: 'already_linked', id_cliente_app: number }
   *       — contato já estava vinculado; backend não altera nada.
   *
   *   - { mode: 'auto', id_cliente_app: number, matched_participants: string[] }
   *       — match único; backend vincula e devolve id + telefones que casaram.
   *
   *   - { mode: 'suggest', id_cliente_app: null, candidates: Candidate[] }
   *       — 2+ matches; UI mostra banner para o usuário escolher.
   *
   *   - { mode: 'none', id_cliente_app: null, candidates: [] }
   *       — sem match; UI mantém CTA "Vincular cliente" + lista de participantes.
   *
   * Candidate:
   *   { id_cliente_app: number, nome_cliente: string, cpf: string|null,
   *     telefone: string|null, matched_participants: string[] }
   *
   * Erros (HTTP sempre 200, status no body):
   *   - { status: 404, value: null, message: "Contato não encontrado" }
   *   - { status: 403, value: null, message: "Acesso negado" }
   *
   * Frontend deve usar `parseAutoLinkResponse(r)` (atendimento.service.js)
   * para normalizar a resposta antes de consumir.
   */
  async autoLinkContato({ id_contato } = {}) {
    const r = await con.post(atendimento.autoLinkContato(id_contato))
    return r || this.fallback()
  }

  // Re-busca os participantes do grupo e, em caso de sucesso, salva no Vuex
  // (action pura applyContatoUpdate atualiza o contato selecionado + lateral).
  async refreshGroupParticipants({ id_contato } = {}) {
    if (!id_contato) {
      return this.fallback('Contato inválido para recarregar participantes.')
    }
    const r = await con.post(atendimento.refreshGroupParticipants(id_contato))
    if (r && r.status === 200 && r.value && Array.isArray(r.value.participantes)) {
      store.dispatch('atendimento/applyContatoUpdate', {
        id_contato,
        value: { participantes: r.value.participantes },
      })
    }
    return r || this.fallback()
  }

  // Recarrega nome + foto do contato a partir do WhatsApp (Evolution). A foto
  // da Evolution expira quando a pessoa troca de imagem; este endpoint força a
  // releitura. Preserva o override manual `nome_contato`. Em caso de sucesso,
  // salva os dados no Vuex.
  async refreshContatoDados({ id_contato } = {}) {
    if (!id_contato) {
      return this.fallback('Contato inválido para recarregar os dados.')
    }
    const r = await con.post(atendimento.refreshContatoDados(id_contato))
    if (r && r.status === 200 && r.value) {
      store.dispatch('atendimento/applyContatoUpdate', {
        id_contato,
        value: r.value,
      })
    }
    return r || this.fallback()
  }

  // Define (ou limpa, com string vazia) o override manual de nome do contato
  // (`nome_contato`). Útil quando o pushName do WhatsApp está errado ou ausente
  // e não se quer vincular o contato a um cliente. Em caso de sucesso, salva no
  // Vuex.
  async renomearContato({ id_contato, nome_contato } = {}) {
    if (!id_contato) {
      return this.fallback('Contato inválido para renomear.')
    }
    const r = await con.post(atendimento.renomearContato(id_contato), {
      nome_contato,
    })
    if (r && r.status === 200 && r.value) {
      store.dispatch('atendimento/applyContatoUpdate', {
        id_contato,
        value: r.value,
      })
    }
    return r || this.fallback()
  }

  async buscarContatos({ q } = {}) {
    const params = { q }
    const r = await con.get(
      atendimento.buscarContatos() + formatQueryParams(params)
    )
    return r || this.fallback()
  }

  // Cria/garante um contato na agenda do atendimento a partir de um
  // participante de grupo. Idempotente no backend: repetir com o mesmo
  // telefone não gera erro (status 200). Telefone LID/inválido → status 400.
  async criarContato({ id_conexao, telefone, nome } = {}) {
    if (!id_conexao || !telefone) {
      return this.fallback(
        'Conexão e telefone são obrigatórios para criar o contato. Recarregue a conversa e tente novamente.'
      )
    }
    const r = await con.post(atendimento.criarContato(), {
      id_conexao,
      telefone,
      nome,
    })
    return r || this.fallback()
  }

  async pollConversas({ since } = {}) {
    const params = { since }
    const r = await con.get(
      atendimento.conversasPoll() + formatQueryParams(params)
    )
    return r || this.fallback()
  }

  async marcarComoLida({ id_conversa } = {}) {
    const r = await con.post(atendimento.marcarLida(id_conversa))
    return r || this.fallback()
  }

  async buscarCliente({ id_cliente } = {}) {
    const r = await con.get(atendimento.clienteDetalhe(id_cliente))
    return r || this.fallback()
  }

  async atribuirConversa({ id_conversa } = {}) {
    const r = await con.post(atendimento.atribuirConversa(id_conversa))
    return r || this.fallback()
  }

  async fecharConversa({ id_conversa } = {}) {
    const r = await con.post(atendimento.fecharConversa(id_conversa))
    return r || this.fallback()
  }

  async escalarConversa({ id_conversa, nota = '' } = {}) {
    const r = await con.post(atendimento.escalarConversa(id_conversa), { nota })
    return r || this.fallback()
  }

  async transferirConversa({
    id_conversa,
    para_profissional_id,
    nota = '',
  } = {}) {
    const r = await con.post(atendimento.transferirConversa(id_conversa), {
      para_profissional_id,
      nota,
    })
    return r || this.fallback()
  }

  async mudarStatus({ id_conversa, status } = {}) {
    const r = await con.put(atendimento.mudarStatus(id_conversa), { status })
    return r || this.fallback()
  }

  async listarRespostasRapidas() {
    const r = await con.get(atendimento.respostasRapidas())
    return r || this.fallback()
  }

  async criarRespostaRapida({ titulo, conteudo } = {}) {
    const r = await con.post(atendimento.respostasRapidas(), {
      titulo,
      conteudo,
    })
    return r || this.fallback()
  }

  async editarRespostaRapida({ id, titulo, conteudo } = {}) {
    const r = await con.put(atendimento.respostaRapida(id), {
      titulo,
      conteudo,
    })
    return r || this.fallback()
  }

  async deletarRespostaRapida({ id } = {}) {
    const r = await con.delet(atendimento.respostaRapida(id))
    return r || this.fallback()
  }

  async getDeliveryStatus({ id_conversa } = {}) {
    const r = await con.get(atendimento.deliveryStatus(id_conversa))
    return r || this.fallback()
  }

  async getAuditoria({
    page = 1,
    per_page = 50,
    acao = '',
    id_profissional = '',
    data_inicio = '',
    data_fim = '',
  } = {}) {
    const params = {
      page,
      per_page,
      acao,
      id_profissional,
      data_inicio,
      data_fim,
    }
    const r = await con.get(atendimento.auditoria() + formatQueryParams(params))
    return r || this.fallback()
  }
}

const apiAtendimento = new ApiAtendimento()

export { apiAtendimento }
export default apiAtendimento
